import { pathnameToCanonical } from "@/lib/i18nRouting";

/**
 * Derives a readable page label from the pathname for analytics.
 * Customize this mapping if product needs tighter alignment with CMS or route meta.
 */
export function pageNameFromLocation(pathname: string, search: string): string {
  const path = pathname && pathname !== "" ? pathname : "/";
  const q = search?.replace(/^\?/, "") ?? "";
  return q ? `${path}?${q}` : path;
}

/** Coarse grouping for reporting (first URL segment or HOME). */
export function pageCategoryFromPathname(pathname: string): string {
  const p = pathname && pathname !== "" ? pathname : "/";
  if (p === "/") return "HOME";
  const seg = p.split("/").filter(Boolean)[0];
  return seg ? seg.replace(/-/g, "_").toUpperCase() : "HOME";
}

export type GtmPageAnalyticsLabels = {
  pageName: string;
  pageCategory: string;
  pageTitle: string;
};

/**
 * Maps URL → stable GTM labels. Uses **canonical** paths (`/en/services` → `/services`).
 * Add or reorder rules here as routes ship — match **specific** segments before prefixes.
 */
export function gtmPageAnalyticsFromPathname(
  pathname: string,
  search: string
): GtmPageAnalyticsLabels {
  const rawPath = pathname && pathname !== "" ? pathname : "/";
  const canonical = pathnameToCanonical(rawPath.split("?")[0] ?? "/").replace(/\/$/, "") || "/";
  const segments = canonical.split("/").filter(Boolean);

  // ─── Dynamic / nested routes (check before static siblings) ───
  if (segments[0] === "service-detail" && segments.length >= 2) {
    return {
      pageName: "Service Detail",
      pageCategory: "SERVICES",
      pageTitle: "Service Detail",
    };
  }
  if (segments[0] === "services" && segments.length >= 2) {
    return {
      pageName: "Service Listing",
      pageCategory: "SERVICES",
      pageTitle: "Service Listing",
    };
  }
  if (segments[0] === "product-detail" && segments.length >= 2) {
    return {
      pageName: "Product Detail",
      pageCategory: "PRODUCTS",
      pageTitle: "Product Detail",
    };
  }
  if (segments[0] === "product" && segments.length >= 2) {
    return {
      pageName: "Products Listing",
      pageCategory: "PRODUCTS",
      pageTitle: "Products Listing",
    };
  }
  if (segments[0] === "offer-detail" && segments.length >= 2) {
    return {
      pageName: "Offer Detail",
      pageCategory: "OFFERS",
      pageTitle: "Offer Detail",
    };
  }
  if (segments[0] === "offers" && segments.length >= 2) {
    return {
      pageName: "Offers Listing",
      pageCategory: "OFFERS",
      pageTitle: "Offers Listing",
    };
  }
  if (segments[0] === "package-detail" && segments.length >= 2) {
    return {
      pageName: "Package Detail",
      pageCategory: "PACKAGES",
      pageTitle: "Package Detail",
    };
  }
  if (segments[0] === "packages" && segments.length >= 2) {
    return {
      pageName: "Packages Listing",
      pageCategory: "PACKAGES",
      pageTitle: "Packages Listing",
    };
  }
  if (segments[0] === "news-detail" && segments.length >= 2) {
    return {
      pageName: "News Detail",
      pageCategory: "NEWS DETAIL",
      pageTitle: "News Detail",
    };
  }
  if (segments[0] === "news" && segments.length >= 2) {
    return {
      pageName: "News Listing",
      pageCategory: "NEWS",
      pageTitle: "News Listing",
    };
  }
  if (segments[0] === "my-garage" && segments.length >= 2) {
    return {
      pageName: "Garage Vehicle",
      pageCategory: "MY GARAGE",
      pageTitle: "Garage Vehicle",
    };
  }

  // ─── Static routes ───
  const staticMap: Record<
    string,
    Pick<GtmPageAnalyticsLabels, "pageName" | "pageCategory" | "pageTitle">
  > = {
    "/": {
      pageName: "Home",
      pageCategory: "HOME",
      pageTitle: "Home",
    },
    "/services": {
      pageName: "Service Listing",
      pageCategory: "SERVICES",
      pageTitle: "Service Listing",
    },
    "/product": {
      pageName: "Products Listing",
      pageCategory: "PRODUCTS",
      pageTitle: "Products Listing",
    },
    "/offers": {
      pageName: "Offers Listing",
      pageCategory: "OFFERS",
      pageTitle: "Offers Listing",
    },
    "/packages": {
      pageName: "Packages Listing",
      pageCategory: "PACKAGES",
      pageTitle: "Packages Listing",
    },
    "/news": {
      pageName: "News Listing",
      pageCategory: "NEWS",
      pageTitle: "News Listing",
    },
    "/about": {
      pageName: "About",
      pageCategory: "ABOUT",
      pageTitle: "About",
    },
    "/contact": {
      pageName: "Contact",
      pageCategory: "CONTACT",
      pageTitle: "Contact",
    },
    "/find-workshop": {
      pageName: "Find Workshop",
      pageCategory: "LOCATOR",
      pageTitle: "Find Workshop",
    },
    "/login": {
      pageName: "Login",
      pageCategory: "ACCOUNT",
      pageTitle: "Login",
    },
    "/my-account": {
      pageName: "My Account",
      pageCategory: "ACCOUNT",
      pageTitle: "My Account",
    },
    "/profile": {
      pageName: "Profile",
      pageCategory: "ACCOUNT",
      pageTitle: "Profile",
    },
    "/my-garage": {
      pageName: "My Garage",
      pageCategory: "MY GARAGE",
      pageTitle: "My Garage",
    },
    "/privacy-policy": {
      pageName: "Privacy Policy",
      pageCategory: "LEGAL",
      pageTitle: "Privacy Policy",
    },
    "/terms-and-conditions": {
      pageName: "Terms and Conditions",
      pageCategory: "LEGAL",
      pageTitle: "Terms of Service",
    },
    "/cookie-policy": {
      pageName: "Cookies",
      pageCategory: "LEGAL",
      pageTitle: "Cookie Policy",
    },
    "/body-paint": {
      pageName: "Body & Paint",
      pageCategory: "ABOUT US",
      pageTitle: "Body & Paint",
    },
    "/ev-services": {
      pageName: "EV Services",
      pageCategory: "ABOUT US",
      pageTitle: "EV Services",
    },
    "/luxury-repair": {
      pageName: "Luxury Repair",
      pageCategory: "ABOUT US",
      pageTitle: "Luxury Repair",
    },
    "/national-oil-stop": {
      pageName: "National Oil Stop",
      pageCategory: "ABOUT US",
      pageTitle: "National Oil Stop",
    },
    "/petromin-autocare": {
      pageName: "Petromin Autocare",
      pageCategory: "ABOUT US",
      pageTitle: "Petromin Autocare",
    },
    "/petromin-commercial": {
      pageName: "Petromin Commercial",
      pageCategory: "ABOUT US",
      pageTitle: "Petromin Commercial",
    },
    "/petromin-express": {
      pageName: "Petromin Express",
      pageCategory: "ABOUT US",
      pageTitle: "Petromin Express",
    },
  };

  const fixed = staticMap[canonical];
  if (fixed) {
    return { ...fixed };
  }

  // ─── Fallback: path-shaped name + segment category ───
  const canonicalForFallback = pathnameToCanonical(rawPath);
  const fallbackPath = pageNameFromLocation(canonicalForFallback, search);
  const fallbackCategory = pageCategoryFromPathname(canonicalForFallback);
  return {
    pageName: fallbackPath,
    pageCategory: fallbackCategory,
    pageTitle: fallbackPath,
  };
}
